import { useCallback, useMemo } from 'react'
import { useGetSettingsQuery, useUpdateSettingsMutation } from '../redux/api/customerApi'
import { useAuth } from './useAuth'
import { DEFAULT_SETTINGS, UserSettings } from '../types/constants'

export interface GridSettings {
  rowsPerPage: number
  filtersVisible: boolean
  columns: GridColumnSettings[]
}

export interface GridColumnSettings {
  key: string
  hide?: boolean
  sortDirection?: 'ASC' | 'DESC'
  filter?: { type: string; value?: unknown }
}

const langs = ['en', 'de', 'fr', 'it']

export function useSettings<T extends keyof UserSettings>(key: T, options?: { remote: boolean }) {
  const { user } = useAuth()
  const remote = options ? options.remote : !!user

  const settings = useGetSettingsQuery({ remote })
  const [updateSettings] = useUpdateSettingsMutation()

  const setting = useMemo(() => {
    const saved = settings.data?.[key]
    if (!saved && key === 'lang' && user?.locale && langs.includes(user.locale)) return user.locale as UserSettings[T]
    return (saved ?? DEFAULT_SETTINGS[key]) as UserSettings[T]
  }, [key, settings.data, user?.locale])

  const saveSetting = useCallback(
    (newSetting: UserSettings[T]) => {
      const settingsEmpty = settings.error && 'isApiException' in settings.error && settings.error.status === 404
      if (settings.data || settingsEmpty) {
        updateSettings({
          remote,
          model: {
            ...settings.data,
            lang: settings.data?.lang ?? DEFAULT_SETTINGS.lang,
            [key]: newSetting,
          },
        })
      }
    },
    [key, remote, settings.data, settings.error, updateSettings]
  )

  return [setting, saveSetting, settings.isLoading] as const
}
